import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import React, {
    useState, useEffect, useMemo,
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@material-ui/core';

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { NextRunLabel } from '../../data-sources/data-set-config/NextRun';

const useStyles = makeStyles((theme) => ({
    container: {
        overflow: 'visible',
    },
    containerFull: {
        overflow: 'visible',
    },
    fab: {
        margin: theme.spacing(2),
    },
    addBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: theme.spacing(0, 0, 1, 0),
    },
    row: {
        cursor: 'pointer',
    },
}));

const SortableRow = ({
    row,
    columns,
    details,
    cellRenderer,
    classes,
    enableSorting,
    savedCron,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: row.code });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: 'default',
    };

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            className={classes.row}
            hover
            tabIndex={-1}
            key={row.code}
            onClick={() => details(row)}
        >
            {enableSorting && (
                <TableCell {...attributes} {...listeners} style={{ cursor: 'grab', width: 40 }}>
                    <span style={{ userSelect: 'none' }}>≡</span>
                </TableCell>
            )}

            {columns.map((column) => {
                let value = row[column.id];
                if (cellRenderer) {
                    value = cellRenderer(column, value, row);
                }
                return (
                    <TableCell key={column.id} align={column.align} style={{ maxWidth: column.id === 'nextRunInfo' ? 150 : 'none' }}>
                        {column.format ? column.format(value) : value}
                        {column.id === 'nextRunInfo' && <NextRunLabel savedCron={savedCron} dataSetConfigId={row.id} />}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

const DataTable = ({
    columns,
    rows,
    add,
    details,
    enableSorting = false,
    tableName = 'data',
    cellRenderer,
    fullHeight = false,
    ...restProps
}) => {
    const classes = useStyles();
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor)
    );

    const [localRows, setLocalRows] = useState([]);
    const [colSort, setColSort] = useState({ id: null, dir: 'asc' });
    const scrollKey = `tableScrollPos-${tableName}`;

    const handleColSort = (columnId) => {
        setColSort((prev) => {
            if (prev.id === columnId) {
                return { id: columnId, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            }
            return { id: columnId, dir: 'asc' };
        });
    };

    const displayRows = useMemo(() => {
        if (!colSort.id) return localRows;
        const col = columns.find((c) => c.id === colSort.id);
        return [...localRows].sort((a, b) => {
            const aVal = col && col.sortValue ? col.sortValue(a) : (a[colSort.id] ?? '');
            const bVal = col && col.sortValue ? col.sortValue(b) : (b[colSort.id] ?? '');
            const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
            return colSort.dir === 'asc' ? cmp : -cmp;
        });
    }, [localRows, colSort, columns, enableSorting]);

    useEffect(() => {
        const savedOrder = localStorage.getItem(`tableOrder-${tableName}`);
        if (savedOrder) {
            const order = JSON.parse(savedOrder);
            const orderedRows = order
                .map((code) => rows.find((r) => r.code === code)).filter(Boolean);
            const missing = rows.filter((r) => !order.includes(r.code));
            setLocalRows([...orderedRows, ...missing]);
        } else {
            setLocalRows(rows);
        }
    }, [rows, tableName]);

    useEffect(() => {
        const savedScroll = localStorage.getItem(scrollKey);
        const scrollY = parseInt(savedScroll || '0', 10);

        if (Number.isNaN(scrollY) || scrollY === 0) return;

        const tryScrollRestore = () => {
            if (document.documentElement.scrollHeight > window.innerHeight) {
                window.scrollTo(0, scrollY);
                return true;
            }
            return false;
        };

        const interval = setInterval(() => {
            const success = tryScrollRestore();
            if (success) {
                clearInterval(interval);
            }
        }, 50);

        setTimeout(() => clearInterval(interval), 2000);
        // eslint-disable-next-line
        return;
    }, [localRows, scrollKey]);

    useEffect(() => {
        const saveScroll = () => {
            localStorage.setItem(scrollKey, window.scrollY);
        };
        window.addEventListener('scroll', saveScroll);
        return () => {
            window.removeEventListener('scroll', saveScroll);
        };
    }, [scrollKey]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;

        if (active.id !== over.id) {
            const oldIndex = localRows.findIndex((item) => item.code === active.id);
            const newIndex = localRows.findIndex((item) => item.code === over.id);
            const updatedRows = arrayMove(localRows, oldIndex, newIndex);
            setLocalRows(updatedRows);

            localStorage.setItem(
                `tableOrder-${tableName}`,
                JSON.stringify(updatedRows.map((r) => r.code))
            );
        }
    };

    return (
        <div className={classes.root}>
            <AddNewButton classes={classes} add={add} {...restProps} />
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={localRows.map((row) => row.id ?? row.code)}
                    strategy={verticalListSortingStrategy}
                >
                    <TableContainer
                        className={fullHeight ? classes.containerFull : classes.container}
                    >
                        <Table aria-label={`${tableName}-table`}>
                            <TableHead>
                                <TableRow>
                                    {enableSorting && <TableCell />}
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{
                                                minWidth: column.minWidth,
                                                maxWidth: column.maxWidth
                                            }}
                                        >
                                            {column.sortable !== false ? (
                                                <TableSortLabel
                                                    active={colSort.id === column.id}
                                                    direction={colSort.id === column.id ? colSort.dir : 'asc'}
                                                    onClick={() => handleColSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {enableSorting && <TableRow />}
                                {displayRows.map((row) => (
                                    <SortableRow
                                        key={row.code}
                                        row={row}
                                        columns={columns}
                                        details={details}
                                        cellRenderer={cellRenderer}
                                        classes={classes}
                                        enableSorting={enableSorting}
                                        savedCron={row.cron}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </SortableContext>
            </DndContext>
        </div>
    );
};

const AddNewButton = ({ classes, add, withoutAddButton }) => {
    if (withoutAddButton || !add) return null;

    return (
        <Box className={classes.addBar}>
            <Button
                aria-label="Add"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={add}
            >
                Add New
            </Button>
        </Box>
    );
};

export default DataTable;
